import {Component, inject, OnInit} from '@angular/core';
import {ModalWindowComponent} from "../../../std-components/modal-window/modal-window.component";
import {CommonModule} from "@angular/common";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {NgxMaskDirective, provideNgxMask} from "ngx-mask";
import {
  IGetBuildingRoom,
  ISetBuildingRoomsFloorValues,
  StagingService
} from "../../../new-information/services/staging.service";
import {RoomTypeEnum} from "../../../shared/models/models";
import {ToastrService} from "ngx-toastr";
import {finalize, forkJoin} from "rxjs";

// 900	0
// 100	-1
// 200	-2
// 300	-3
// 400	-4
// 500	-5

const parkingMap = {
  1: 900, //0
  2: 100, // -1
  3: 200, // -2
  4: 300, // -3
  5: 400, // -4
  6: 500, // -5
}

@Component({
  selector: 'app-create-parking',
  standalone: true,
  imports: [
    ModalWindowComponent,
    CommonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
  ],
  templateUrl: './create-parking.component.html',
  styleUrl: './create-parking.component.scss',
  providers: [
    provideNgxMask()
  ]
})
export class CreateParkingComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<CreateParkingComponent>);
  readonly data = inject<{ buildingID: number }>(MAT_DIALOG_DATA);

  currentStep: StepEnum = StepEnum.FirstStep;
  protected readonly StepEnum = StepEnum;
  /*11 и 12 (Подземный, Наземный)*/
  formParking = new FormGroup({
    parking1: new FormControl(null, [Validators.min(1), Validators.max(6)]), /*Наземный - 12*/
    parking2: new FormControl(null, [Validators.min(1), Validators.max(6)]) /*Подземный - 11*/
  });

  formParkingList = new FormGroup({
    parkingList1: new FormArray([]),
    parkingList2: new FormArray([])
  });

  /*Наземный*/
  get parking1(): FormControl {
    return this.formParking.get('parking1') as FormControl;
  }

  /*Подземный*/
  get parking2(): FormControl {
    return this.formParking.get('parking2') as FormControl;
  }

  get parkingList1(): FormArray {
    return this.formParkingList.get('parkingList1') as FormArray;
  }

  get parkingList2(): FormArray {
    return this.formParkingList.get('parkingList2') as FormArray;
  }

  parkingRooms: IGetBuildingRoom[] = [];

  constructor(private stagingService: StagingService,
              private toastr: ToastrService) {
  }

  loading = false;

  ngOnInit() {
    this.loading = true;

    this.stagingService.getBuildingRooms(this.data.buildingID, RoomTypeEnum.Parking)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (res) => {
          this.parkingRooms = res;
          this.setParkingValuesFromApi(res);
        },
        error: (err) => {
          console.error('Ошибка при получении данных:', err);
        }
      });
  }

  setParkingValuesFromApi(data: IGetBuildingRoom[]): void {
    const ground = data.filter(item => item.roomTypeItemID === ParkingTypeEnum.Ground);
    const underground = data.filter(item => item.roomTypeItemID === ParkingTypeEnum.Underground);

    const uniqueGroundFloors = new Set(ground.map(item => item.floorID));
    const uniqueUndergroundFloors = new Set(underground.map(item => item.floorID));

    const groundCount = uniqueGroundFloors.size;
    const undergroundCount = uniqueUndergroundFloors.size;

    // Наземный
    this.parking1.setValue(groundCount > 0 ? groundCount : null);
    this.parking1.setValidators([
      Validators.min(Math.max(1, groundCount)),
      Validators.max(6),
    ]);
    this.parking1.updateValueAndValidity();

    // Подземный
    this.parking2.setValue(undergroundCount > 0 ? undergroundCount : null);
    this.parking2.setValidators([
      Validators.min(Math.max(1, undergroundCount)),
      Validators.max(6),
    ]);
    this.parking2.updateValueAndValidity();
  }

  get isNextDisabled(): boolean {
    return (
      (!this.parking1?.value && !this.parking2?.value) ||
      this.parking1?.invalid ||
      this.parking2?.invalid
    );
  }



  getParkingControl1(i: number): FormControl {
    return this.parkingList1.at(i) as FormControl;
  }

  getParkingControl2(i: number): FormControl {
    return this.parkingList2.at(i) as FormControl;
  }


  prevStep() {
    this.currentStep = StepEnum.FirstStep;
  }


  existParking1: {floorID: number, placeCount: number}[] = [];
  existParking2: {floorID: number, placeCount: number}[] = [];
  nextStep() {
    const count1 = this.parking1.value;
    const count2 = this.parking2.value;

    this.parkingList1.clear();
    this.parkingList2.clear();

    this.existParking1 = [];
    this.existParking2 = [];

    this.mappingParking(count1, this.parkingList1, ParkingTypeEnum.Ground, this.existParking1);
    this.mappingParking(count2, this.parkingList2, ParkingTypeEnum.Underground, this.existParking2);

    this.currentStep = StepEnum.SecondStep;
  }

  mappingParking(count: number, parkingList: FormArray, parkingType: ParkingTypeEnum, existParking: {floorID: number, placeCount: number}[] = []): void {
    const parkingValues = this.parkingRooms.filter(item => item.roomTypeItemID === parkingType);
    const sortedFloors = Array.from(new Set(parkingValues.map(item => item.floorID)))
      .sort((a, b) => a - b); // сортировка по возрастанию
    if (parkingValues.length) {
      for (const floor of sortedFloors) {
        const placesCount = parkingValues.filter(f => f.floorID === floor).length;
        parkingList.push(new FormControl(0, Validators.required));
        existParking.push({floorID: floor, placeCount: placesCount});
      }
      const lastFloor = sortedFloors[sortedFloors.length - 1];
      for (let i = 1; i <= count-sortedFloors.length; i++) {
        parkingList.push(new FormControl(0, Validators.required));
        existParking.push({floorID: lastFloor+i, placeCount: 0});
      }
    } else {
      for (let i = 0; i < count; i++) {
        parkingList.push(new FormControl(0, Validators.required));
        existParking.push({floorID: i, placeCount: 0});
      }
    }
  }

  saveLoading = false;
  save(): void {
    if (this.formParking.invalid) {
      this.formParking.markAllAsTouched();
      return;
    }

    // this.saveLoading = true

    /*Наземный*/
    const parking1Values: {floorID: number, placeCount: number}[] = this.parkingList1.controls
      .map((control, index) => ({ value: control.value, dirty: control.dirty, index: index+1 }))
      .filter(control => control.dirty)
      .map(control => ({floorID: control.index, placeCount: control.value}));

    const payload1: ISetBuildingRoomsFloorValues[] = parking1Values.map(({floorID, placeCount}) => {
      return {
        buildingID: this.data.buildingID,
        roomTypeID: RoomTypeEnum.Parking,
        roomTypeItemID: ParkingTypeEnum.Ground,
        floorID: parkingMap[floorID],
        placeCount
      };
    });

    /*Подземный*/
    const parking2Values: {floorID: number, placeCount: number}[] = this.parkingList2.controls
      .map((control, index) => ({ value: control.value, dirty: control.dirty, index: index+1 }))
      .filter(control => control.dirty)
      .map(control => ({floorID: control.index, placeCount: control.value}));

    const payload2: ISetBuildingRoomsFloorValues[] = parking2Values.map(({floorID, placeCount}) => {
      return {
        buildingID: this.data.buildingID,
        roomTypeID: RoomTypeEnum.Parking,
        roomTypeItemID:ParkingTypeEnum.Underground,
        floorID: parkingMap[floorID],
        placeCount,
      };
    });

    const allPayloads: ISetBuildingRoomsFloorValues[] = [...payload1, ...payload2];

    const requests = allPayloads.map(item =>
      this.stagingService.setBuildingRoomsFloorValues(item)
    );

    forkJoin(requests)
      .pipe(finalize(() => this.saveLoading = false))
      .subscribe({
      next: () => {
        this.toastr.success('Парковочные места успешно созданы!');
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error('Ошибка при сохранении:', err);
      },
    });
  }


  close() {
    this.dialogRef.close();
  }
}

enum StepEnum {
  FirstStep = 1,
  SecondStep = 2
}

export enum ParkingTypeEnum {
  Ground = 12, /*Наземный*/
  Underground = 11, /*Подземный*/
}
